import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PrintAuthUrlService {
  private readonly http = inject(HttpClient);

  openAuthenticatedPrint(
    url: string,
    features = 'location=yes,height=1847,width=1002,scrollbars=yes,status=yes'
  ): void {
    const printWindow = window.open('', '_blank', features);
    if (!printWindow) {
      window.alert('The print preview was blocked. Please allow pop-ups and retry.');
      return;
    }

    this.showLoadingState(printWindow);
    this.http.get(url, { observe: 'response', responseType: 'blob' }).subscribe({
      next: response => this.renderResponse(printWindow, url, response),
      error: () => this.handleFailure(printWindow)
    });
  }

  private renderResponse(printWindow: Window, sourceUrl: string, response: HttpResponse<Blob>): void {
    if (printWindow.closed || !response.body) {
      return;
    }

    const contentType = response.headers.get('content-type') || response.body.type || 'application/pdf';
    const responseBody = response.body.type
      ? response.body
      : new Blob([response.body], { type: contentType });

    if (contentType.toLowerCase().includes('text/html')) {
      responseBody.text()
        .then(html => this.renderHtml(printWindow, sourceUrl, html))
        .catch(() => this.handleFailure(printWindow));
      return;
    }

    const objectUrl = URL.createObjectURL(responseBody);
    printWindow.location.replace(objectUrl);
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 5 * 60 * 1000);
  }

  private renderHtml(printWindow: Window, sourceUrl: string, html: string): void {
    if (printWindow.closed) {
      return;
    }

    const baseUrl = new URL('.', sourceUrl).href.replace(/"/g, '&quot;');
    const baseElement = `<base href="${baseUrl}">`;
    const htmlWithBase = /<head(?:\s[^>]*)?>/i.test(html)
      ? html.replace(/<head(\s[^>]*)?>/i, match => `${match}${baseElement}`)
      : `${baseElement}${html}`;

    printWindow.document.open();
    printWindow.document.write(htmlWithBase);
    printWindow.document.close();
  }

  private showLoadingState(printWindow: Window): void {
    printWindow.document.title = 'Preparing print preview';
    printWindow.document.body.textContent = 'Preparing print preview…';
  }

  private handleFailure(printWindow: Window): void {
    if (!printWindow.closed) {
      printWindow.close();
    }
    window.alert('Unable to open the print preview. Please retry.');
  }
}
